import { THEME_APP_EMBED_BLOCK_HANDLE } from '../../../server/channel/channel.constants';
import { IMPERSONATION_COOKIE } from '../../../server/auth/impersonation.constants';

// The embedded app knows its shop from App Bridge (window.shopify.config.shop)
// or, failing that, the `shop` URL query param Shopify appends on load.
// Lives outside _app.tsx so components (e.g. SetupGuide's checkout-editor deep
// link) can build admin URLs without dragging the whole app shell into tests.
export function resolveShopDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const fromBridge = readAppBridgeShop();
  if (fromBridge) return fromBridge;
  const fromQuery = new URLSearchParams(window.location.search).get('shop');
  return fromQuery ?? undefined;
}

/**
 * Deep link into the checkout editor on the Thank you page with the apps panel open — where
 * the merchant adds the "Tuneboom Downloads" block. No profile id in the URL: reading one
 * needs the read_checkout_branding_settings scope this app does not hold, and the id-less
 * editor URL resolves to the published profile.
 */
export function checkoutEditorUrl(shopDomain: string): string {
  return (
    `https://admin.shopify.com/store/${shopDomain.replace('.myshopify.com', '')}` +
    '/settings/checkout/editor?page=thank-you&context=apps'
  );
}

/**
 * Deep link into the theme editor with the unified Tuneboom app embed pre-activated — the
 * merchant only has to press Save. Same URL the onboarding guide's embed step builds
 * server-side (OnboardingService.appEmbedStep); `activateAppId` is
 * `<app api key>/<embed handle>` — the api key is per-app, so staging and production links
 * each point at their own app's embed.
 */
export function appEmbedEditorUrl(shopDomain: string): string {
  return (
    `https://${shopDomain}/admin/themes/current/editor` +
    `?context=apps&activateAppId=${process.env.SHOPIFY_API_KEY}/${THEME_APP_EMBED_BLOCK_HANDLE}`
  );
}

// App Bridge's script tag exposes `window.shopify.config.shop`. It isn't on the
// DOM lib types, so narrow step by step rather than asserting a shape.
function readAppBridgeShop(): string | undefined {
  if (!('shopify' in window)) return undefined;
  const bridge = window.shopify;
  if (bridge === null || typeof bridge !== 'object' || !('config' in bridge)) return undefined;
  const config = bridge.config;
  if (config === null || typeof config !== 'object' || !('shop' in config)) return undefined;
  const shop = config.shop;
  return typeof shop === 'string' && shop ? shop : undefined;
}

/**
 * The shop of the active admin impersonation session (from the readable
 * {@link IMPERSONATION_COOKIE}), or undefined when not impersonating. Lets the
 * app shell render standalone and show an "impersonating" banner. The auth token
 * lives in the same cookie but is consumed only by the `_document` headless stub.
 */
export function impersonationShop(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match(new RegExp(`(?:^|; )${IMPERSONATION_COOKIE}=([^;]+)`));
  if (!m) return undefined;
  try {
    let b = m[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b.length % 4) b += '=';
    const parsed = JSON.parse(decodeURIComponent(escape(atob(b))));
    return parsed && typeof parsed.shop === 'string' ? parsed.shop : undefined;
  } catch {
    return undefined;
  }
}
